"use client";

import { useTranslations } from "next-intl";
import { useWorkspace } from "@/app/components/workspace/WorkspaceContext";
import { Button } from "@/app/components/ui/Button";
import { Input } from "@/app/components/ui/Input";
import { Select } from "@/app/components/ui/Select";
import { CourierPlatformTiles } from "@/app/modules/courier/components/CourierPlatformTiles";
import { CourierTeamColorField } from "@/app/modules/courier/components/CourierTeamColorField";
import { getCourierPlatformLabel } from "@/app/modules/courier/platforms";
import { removeUnusedCourierVehicles, resolveCourierSelections, validCourierMembers, validCourierPlatforms, validCourierSetup } from "./courierSetup";
import type { CourierSetup, CourierSetupMember } from "./courierSetup";
import type { IndustrySetupFieldsProps } from "./types";

export function CourierSetupFields({ input, context, step = 0, onChange }: IndustrySetupFieldsProps) {
  const t = useTranslations("apps.industrySetup.courier");
  const platformText = useTranslations("courier.components.preferences");
  const { user } = useWorkspace();
  const setup = input.courierSetup;
  const { selections } = resolveCourierSelections(context, input);
  if (!setup) return null;
  const employees = context.courierEmployees ?? [];
  const existingVehicles = context.courierVehicles ?? [];
  const update = (patch: Partial<CourierSetup>) => onChange({ courierSetup: removeUnusedCourierVehicles({ ...setup, ...patch }) });
  const updateMember = (key: string, patch: Partial<CourierSetupMember>) => update({
    members: setup.members.map((member) => member.key === key ? { ...member, ...patch } : member),
  });
  const chooseVehicle = (key: string, value: string) => {
    let vehicles = setup.vehicles;
    let vehicleKey: string | null = value || null;
    if (value === "new") {
      vehicleKey = `vehicle-${crypto.randomUUID()}`;
      vehicles = [...vehicles, { key: vehicleKey, name: "", existingUuid: null }];
    } else if (value.startsWith("existing:")) {
      const uuid = value.slice("existing:".length);
      const existing = existingVehicles.find((vehicle) => vehicle.uuid === uuid);
      if (!existing) return;
      vehicleKey = setup.vehicles.find((vehicle) => vehicle.existingUuid === uuid)?.key ?? `vehicle-${crypto.randomUUID()}`;
      if (!vehicles.some((vehicle) => vehicle.key === vehicleKey)) vehicles = [...vehicles, { key: vehicleKey, name: existing.name, existingUuid: uuid }];
    }
    update({ vehicles, members: setup.members.map((member) => member.key === key ? { ...member, vehicleKey } : member) });
  };

  return <div className="space-y-6">
    {step === 0 ? <>
      {context.companies.length > 1 ? <Select label={t("company")} required value={selections.companyUuid ?? ""}
        onChange={(event) => onChange({ companyUuid: event.target.value || null })}>
        <option value="">{t("chooseCompany")}</option>
        {context.companies.map((company) => <option key={company.uuid} value={company.uuid}>{company.name}</option>)}
      </Select> : null}
      {!input.installOnly ? <>
        <div role="group" aria-label={t("mode")} className="grid grid-cols-2 gap-3">
          {(["solo", "team"] as const).map((mode) => <button key={mode} type="button" aria-pressed={setup.mode === mode}
            disabled={mode === "solo" && setup.members.length > 1}
            className={`rounded-[20px] border-2 p-4 text-left transition-colors disabled:opacity-40 ${setup.mode === mode ? "border-primary bg-primary/5" : "border-transparent bg-bg-sunken hover:bg-surface-hover"}`}
            onClick={() => update({ mode })}>
            <span className="block text-sm font-bold">{t(mode)}</span>
            <span className="mt-1 block text-xs leading-5 text-text-secondary">{t(`${mode}Description`)}</span>
          </button>)}
        </div>
        {setup.members.map((member, index) => <fieldset key={member.key} className="space-y-4 rounded-[24px] bg-bg-sunken/60 p-4 sm:p-5">
          <legend className="px-2 text-xs font-semibold text-text-secondary">{t("member", { number: index + 1 })}</legend>
          <div className="flex flex-wrap items-end gap-3">
            <Input className="min-w-0 flex-1" label={t("name")} required maxLength={160} value={member.name}
              onChange={(event) => updateMember(member.key, { name: event.target.value, employeeUuid: null })} />
            {index === 0 ? <Button type="button" size="sm" variant="secondary" onClick={() => updateMember(member.key, {
              name: user.name?.trim() || member.name, email: user.email, employeeUuid: null,
            })}>{t("addMe")}</Button> : <Button type="button" size="sm" variant="ghost" aria-label={t("removeMember", { number: index + 1 })}
              onClick={() => update({ members: setup.members.filter((item) => item.key !== member.key) })}>{t("remove")}</Button>}
          </div>
          {employees.length > 0 ? <Select label={t("existingEmployee")} value={member.employeeUuid ?? ""} onChange={(event) => {
            const employee = employees.find((item) => item.uuid === event.target.value);
            updateMember(member.key, { employeeUuid: employee?.uuid ?? null, ...(employee ? { name: employee.name, phone: null, email: null } : {}) });
          }}>
            <option value="">{t("manualMember")}</option>
            {employees.map((employee) => <option key={employee.uuid} value={employee.uuid} disabled={setup.members.some((item) => item.key !== member.key && item.employeeUuid === employee.uuid)}>{employee.name}</option>)}
          </Select> : null}
          {!member.employeeUuid ? <details>
            <summary className="text-sm font-medium text-text-secondary">{t("optionalContacts")}</summary>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Input label={t("phone")} type="tel" maxLength={40} value={member.phone ?? ""} onChange={(event) => updateMember(member.key, { phone: event.target.value || null })} />
              <Input label={t("email")} type="email" maxLength={254} value={member.email ?? ""} onChange={(event) => updateMember(member.key, { email: event.target.value || null })} />
            </div>
          </details> : null}
        </fieldset>)}
        {setup.mode === "team" ? <Button type="button" variant="secondary" disabled={setup.members.length >= 30} onClick={() => update({
          members: [...setup.members, { key: `courier-${crypto.randomUUID()}`, name: "", platforms: [...setup.members[0].platforms], vehicleKey: null }],
        })}>{t("addMember")}</Button> : null}
        <details><summary className="text-sm font-medium text-text-secondary">{t("appearance")}</summary><div className="mt-4"><CourierTeamColorField value={setup.color ?? null} onChange={(color) => update({ color })} /></div></details>
        {!validCourierMembers(setup) ? <p role="status" className="text-sm text-text-secondary">{t("membersValidation")}</p> : null}
      </> : null}
    </> : null}

    {step === 1 && !input.installOnly ? <>
      <p className="text-sm leading-6 text-text-secondary">{t("platformsDescription")}</p>
      <CourierPlatformTiles value={setup.members[0].platforms} onChange={(platforms) => update({ members: setup.members.map((member) => ({ ...member, platforms })) })} />
      {setup.members.length > 1 ? <details><summary className="text-sm font-medium">{t("individualPlatforms")}</summary>
        <div className="mt-4 space-y-5">{setup.members.map((member) => <div key={member.key} className="space-y-3 rounded-[20px] bg-bg-sunken/60 p-4">
          <p className="font-semibold">{member.name}</p><CourierPlatformTiles value={member.platforms} onChange={(platforms) => updateMember(member.key, { platforms })} />
        </div>)}</div>
      </details> : null}
      {!validCourierPlatforms(setup) ? <p role="status" className="text-sm text-text-secondary">{t("platformsValidation")}</p> : null}
    </> : null}

    {step === 2 && !input.installOnly ? <>
      <p className="text-sm leading-6 text-text-secondary">{t("vehiclesDescription")}</p>
      {setup.members.map((member) => {
        const selected = setup.vehicles.find((vehicle) => vehicle.key === member.vehicleKey);
        return <fieldset key={member.key} className="space-y-4 rounded-[24px] bg-bg-sunken/60 p-4 sm:p-5">
          <legend className="px-2 text-sm font-semibold">{member.name}</legend>
          <Select label={t("vehicle")} value={member.vehicleKey ?? ""} onChange={(event) => chooseVehicle(member.key, event.target.value)}>
            <option value="">{t("noVehicle")}</option>
            <option value="new">{t("newVehicle")}</option>
            {setup.vehicles.map((vehicle) => <option key={vehicle.key} value={vehicle.key}>{vehicle.name || t("unnamedVehicle")}</option>)}
            {existingVehicles.filter((vehicle) => !setup.vehicles.some((item) => item.existingUuid === vehicle.uuid)).map((vehicle) => <option key={vehicle.uuid} value={`existing:${vehicle.uuid}`}>{vehicle.name}</option>)}
          </Select>
          {selected && !selected.existingUuid ? <Input label={t("vehicleName")} required maxLength={190} placeholder={t("vehiclePlaceholder")} value={selected.name}
            onChange={(event) => update({ vehicles: setup.vehicles.map((vehicle) => vehicle.key === selected.key ? { ...vehicle, name: event.target.value } : vehicle) })} /> : null}
          {selected ? <p className="text-xs leading-5 text-text-secondary">{t("vehicleLinked")}</p> : null}
        </fieldset>;
      })}
      <Button type="button" variant="ghost" onClick={() => update({ vehicles: [], members: setup.members.map((member) => ({ ...member, vehicleKey: null })) })}>{t("vehiclesLater")}</Button>
      {!validCourierSetup(input) ? <p role="status" className="text-sm text-text-secondary">{t("vehiclesValidation")}</p> : null}
    </> : null}

    {step === 3 && !input.installOnly ? <>
      <div className="rounded-[24px] bg-primary/5 p-5 sm:p-6">
        <div className="flex items-center gap-3"><span aria-hidden="true" className="h-10 w-10 shrink-0 rounded-2xl" style={{ backgroundColor: setup.color ?? "#6366F1" }} /><p className="text-lg font-bold">{input.displayName}</p></div>
        <p className="mt-3 text-sm text-text-secondary">{t("summary", { members: setup.members.length, vehicles: setup.vehicles.length })}</p>
        <ul className="mt-5 divide-y divide-border/60">{setup.members.map((member) => <li key={member.key} className="py-3 text-sm">
          <p className="font-semibold">{member.name}</p>
          <p className="mt-1 text-text-secondary">{member.platforms.map((platform) => getCourierPlatformLabel(platform, platformText)).join(", ")}</p>
          <p className="mt-1 text-text-secondary">{setup.vehicles.find((vehicle) => vehicle.key === member.vehicleKey)?.name ?? t("noVehicle")}</p>
        </li>)}</ul>
      </div>
      <p className="text-sm leading-6 text-text-secondary">{t("readyDescription")}</p>
      <p className="text-xs leading-5 text-text-secondary">{t("resumeDescription")}</p>
    </> : null}
  </div>;
}
